// Sticky add-to-cart bar (lm-). Brand replacement for Horizon's native
// <sticky-add-to-cart> (disabled via enable_sticky_add_to_cart:false in
// product.json) so we control the styling and — critically — the
// lm-bundle-tiers interaction.
//
// Bundle products: lm-bundle-tiers hides the native buy form (display:none) and
// adds via its own /cart/add flow. So the sticky button does NOT own cart logic;
// it PROXIES whichever add control is actually live at click time — the visible
// bundle "Add" button if present, else the native add-to-cart button. This
// routes through the correct flow without duplicating it.
//
// Degrades gracefully (62% IG WebView): no IntersectionObserver / no scroll
// anchor / no add button => the bar stays off-screen (ships translated down) and
// the in-page controls remain the only path. Nothing here throws into the page.

if (!(window.customElements && customElements.get('lm-sticky-atc'))) {
  class LmStickyAtc extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-lm-satc-bar]');
      this.button = this.querySelector('[data-lm-satc-add]');
      if (!this.bar || !this.button) return;

      // Scroll anchor: the bundle widget when present (it replaces the native buy
      // box on bundle products), else the native buy-buttons block.
      this.anchor =
        document.querySelector('lm-bundle-tiers') || document.querySelector('.buy-buttons-block');
      if (!this.anchor) return;

      // IntersectionObserver / measurement target. .buy-buttons-block is an inline
      // <span> (its width:100% is ignored on an inline box) wrapping block-level
      // children — its getBoundingClientRect is unreliable across engines, which is
      // why the bar showed on desktop but never on mobile. Observe a guaranteed
      // block-level node instead: the inner .product-form-buttons <div> on standard
      // products, the (display:block) bundle widget otherwise. Falls back to the
      // anchor itself if neither is found.
      this.observeTarget = this.anchor.querySelector('.product-form-buttons') || this.anchor;

      this.stuck = false;
      this.keyboardOpen = false;
      this.drawerOpen = false;

      this._onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this._onClick);

      // Hide while a text field is focused so the on-screen keyboard doesn't
      // collide with a bottom-fixed bar (IG WebView jank).
      this._onFocusIn = this.onFocusIn.bind(this);
      this._onFocusOut = this.onFocusOut.bind(this);
      document.addEventListener('focusin', this._onFocusIn);
      document.addEventListener('focusout', this._onFocusOut);

      // Re-hide when a cart add fires (drawer opens). Belt-and-suspenders — the
      // click handler also hides directly, so this works even if the event name
      // differs across Horizon versions.
      this._onCartUpdate = this.hideForDrawer.bind(this);
      document.addEventListener('cart:update', this._onCartUpdate);
      document.addEventListener('cart:add', this._onCartUpdate);

      if (!('IntersectionObserver' in window)) return; // no-IO: bar stays hidden

      this.io = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0 });
      this.io.observe(this.observeTarget);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
      document.removeEventListener('focusin', this._onFocusIn);
      document.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('cart:update', this._onCartUpdate);
      document.removeEventListener('cart:add', this._onCartUpdate);
    }

    onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;
      // Show only once the buy box has scrolled ABOVE the viewport top — not while
      // it's still below the fold on initial load. Use the observer-provided
      // boundingClientRect (no separate read) and test top <= 0: when not
      // intersecting AND its top edge is above the viewport, the box is fully past;
      // below the fold the top is positive, so the bar stays hidden.
      this.stuck = !entry.isIntersecting && entry.boundingClientRect.top <= 0;
      this.render();
    }

    onFocusIn(e) {
      const t = e.target;
      if (!t) return;
      // Only hide for fields that summon the on-screen keyboard — the collision we
      // actually care about. A variant <select> or radio, a stepper button, etc.
      // don't summon one, so hiding for them just flickered the bar away on tap.
      const typing =
        t.tagName === 'TEXTAREA' ||
        t.isContentEditable ||
        (t.tagName === 'INPUT' &&
          /^(text|search|email|url|tel|number|password)$/i.test(t.type || 'text'));
      if (typing) {
        this.keyboardOpen = true;
        this.render();
      }
    }

    onFocusOut() {
      this.keyboardOpen = false;
      this.render();
    }

    hideForDrawer() {
      this.drawerOpen = true;
      this.render();
      // Let it return after the drawer is dismissed and the shopper scrolls.
      window.setTimeout(() => {
        this.drawerOpen = false;
      }, 1200);
    }

    render() {
      const show = this.stuck && !this.keyboardOpen && !this.drawerOpen;
      this.bar.setAttribute('data-stuck', show ? 'true' : 'false');
    }

    // Resolve the live add control at click time (bundle reveals its button via
    // JS after load, so this must be lazy).
    activeAddButton() {
      const bundleBtn = document.querySelector('[data-lm-bundle-add]');
      if (bundleBtn && !bundleBtn.hidden && bundleBtn.offsetParent !== null) return bundleBtn;
      return (
        document.querySelector('[ref="addToCartButton"]') ||
        document.querySelector('.buy-buttons-block button[name="add"]')
      );
    }

    onClick() {
      const target = this.activeAddButton();
      if (target) {
        target.click();
        this.hideForDrawer();
      } else if (this.anchor && this.anchor.scrollIntoView) {
        // Last resort: bring the shopper to the buy box.
        this.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  customElements.define('lm-sticky-atc', LmStickyAtc);
}
